import React, { useState, useEffect } from "react";
import { doc, updateDoc, arrayUnion, getDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth } from "../../../contexts/AuthContext";
import { verbFormQuestions } from "../../../data/grammarQuestions/verbFormQuestions";
import { advancedVerbFormQuestions } from "../../../data/grammarQuestions/advancedVerbFormQuestions";
import conditionalQuestions from "../../../data/grammarQuestions/conditionalQuestions";
import conjunctionQuestions from "../../../data/grammarQuestions/conjunctionQuestions";
import conjunctionQuestions2 from "../../../data/grammarQuestions/conjunctionQuestions2";
import { tensesQuestions } from "../../../data/grammarQuestions/tensesQuestions";
import { voiceQuestions } from "../../../data/grammarQuestions/voiceQuestions";
import { subjectVerbAgreementQuestions } from "../../../data/grammarQuestions/subjectVerbAgreementQuestions";
import { relativeQuestions } from "../../../data/grammarQuestions/relativeQuestions";
import { comparativeQuestions } from "../../../data/grammarQuestions/comparativeQuestions";
import { collocationQuestions } from "../../../data/grammarQuestions/collocationQuestions";
import { pronounQuestions } from "../../../data/grammarQuestions/pronounQuestions";
import { gerundInfinitiveQuestions } from "../../../data/grammarQuestions/gerundInfinitiveQuestions";
import { businessEmailQuestions } from "../../../data/grammarQuestions/businessEmailQuestions";
import { articleQuestions } from "../../../data/grammarQuestions/articleQuestions";
import { businessEmail2Questions } from "../../../data/grammarQuestions/businessEmail2Questions";
import { businessEmail3Questions } from "../../../data/grammarQuestions/businessEmail3Questions";
import { businessEmail4Questions } from "../../../data/grammarQuestions/businessEmail4Questions";
import { article2Questions } from "../../../data/grammarQuestions/article2Questions";
import { noticeAd2Questions } from "../../../data/grammarQuestions/noticeAd2Questions";
import { noticeAd3Questions } from "../../../data/grammarQuestions/noticeAd3Questions";
import "../../../styles/Part5_Grammar.css";
import Modal from "react-modal";

const Part5_Grammar = ({ categoryId, studyMode, onBack }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [showKoreanQuestion, setShowKoreanQuestion] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { currentUser } = useAuth();
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  const getQuestionsByCategory = (categoryId) => {
    switch (categoryId) {
      case 1:
        return tensesQuestions;
      case 2:
        return voiceQuestions;
      case 3:
        return subjectVerbAgreementQuestions;
      case 4:
        return conditionalQuestions;
      case 5:
        return relativeQuestions;
      case 6:
        return comparativeQuestions;
      case 7:
        return conjunctionQuestions;
      case 8:
        return conjunctionQuestions2;
      case 9:
        return pronounQuestions;
      case 10:
        return advancedVerbFormQuestions;
      case 11:
        return gerundInfinitiveQuestions;
      case 14:
        return collocationQuestions;
      case 15:
        return businessEmailQuestions;
      case 101:
        return businessEmail2Questions;
      case 102:
        return businessEmail3Questions;
      case 103:
        return businessEmail4Questions;
      case 104:
        return article2Questions;
      case 105:
        return noticeAd2Questions;
      case 106:
        return noticeAd3Questions;
      case 16:
        return articleQuestions;
      default:
        return tensesQuestions;
    }
  };

  const filterWrongQuestions = async (allQuestions) => {
    if (!currentUser || studyMode !== "wrong") return allQuestions;

    try {
      const userDocRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) return allQuestions;

      const data = userDoc.data();
      const wrongQuestionIds =
        data?.part5?.grammar?.[categoryId]?.wrongQuestions || [];

      return allQuestions.filter((q) => wrongQuestionIds.includes(q.id));
    } catch (error) {
      console.error("틀린 문제 필터링 중 오류:", error);
      return allQuestions;
    }
  };

  useEffect(() => {
    const loadQuestions = async () => {
      setIsLoading(true);
      try {
        const allQuestions = getQuestionsByCategory(categoryId) || [];
        const filteredQuestions = await filterWrongQuestions(allQuestions);

        // 문제를 랜덤하게 섞는 코드 제거
        setQuestions(filteredQuestions);
      } catch (error) {
        console.error("문제 로딩 중 오류:", error);
      }
      setIsLoading(false);
    };

    loadQuestions();
  }, [categoryId, studyMode, currentUser]);

  if (isLoading) {
    return <div className="spinner"></div>;
  }

  // 틀린 문제 모드에서 문제가 없을 때
  if (studyMode === "wrong" && questions.length === 0) {
    return (
      <div className="no-questions-container">
        <p>축하합니다! 모든 틀린 문제를 잘 맞추셨습니다! 🎉</p>
        <button onClick={onBack} className="back-button">
          돌아가기
        </button>
      </div>
    );
  }

  // 전체 문제 모드에서 문제가 없을 때
  if (studyMode === "all" && questions.length === 0) {
    return (
      <div className="no-questions-container">
        <p>해당 카테고리의 문제를 준비 중입니다.</p>
        <button onClick={onBack} className="back-button">
          돌아가기
        </button>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  const handleAnswerSelect = (answer) => {
    if (!showExplanation) {
      setSelectedAnswer(answer);
    }
  };

  const saveWrongAnswer = async (questionId) => {
    if (!currentUser) return;

    try {
      const userDocRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        await updateDoc(userDocRef, {
          [`part5.grammar.${categoryId}.wrongQuestions`]: [questionId],
        });
      } else {
        await updateDoc(userDocRef, {
          [`part5.grammar.${categoryId}.wrongQuestions`]:
            arrayUnion(questionId),
        });
      }
    } catch (error) {
      console.error("틀린 문제 저장 중 오류:", error);
    }
  };

  const removeFromWrongAnswers = async (questionId) => {
    if (!currentUser) return;

    try {
      const userDocRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) return;

      const data = userDoc.data();
      const wrongQuestions =
        data?.part5?.grammar?.[categoryId]?.wrongQuestions || [];
      const updatedWrongQuestions = wrongQuestions.filter(
        (id) => id !== questionId
      );

      await updateDoc(userDocRef, {
        [`part5.grammar.${categoryId}.wrongQuestions`]: updatedWrongQuestions,
      });

      if (studyMode === "wrong") {
        const updatedQuestions = questions.filter((q) => q.id !== questionId);
        setQuestions(updatedQuestions);

        // 모든 문제를 맞췄을 경우
        if (updatedQuestions.length === 0) {
          setShowCompletionModal(true);
        } else if (currentQuestionIndex >= updatedQuestions.length) {
          setCurrentQuestionIndex(Math.max(0, updatedQuestions.length - 1));
        }
      }
    } catch (error) {
      console.error("틀린 문제 제거 중 오류:", error);
    }
  };

  const markCategoryAsCompleted = async () => {
    if (!currentUser) return;

    try {
      const userDocRef = doc(db, "users", currentUser.uid);

      // 데이터 업데이트
      await updateDoc(userDocRef, {
        [`part5.grammar.${categoryId}.completed`]: true,
      });

      // 업데이트 확인
      const updatedDoc = await getDoc(userDocRef);
      const updatedData = updatedDoc.data();
    } catch (error) {
      console.error("완료 상태 저장 중 오류:", error);
    }
  };

  const handleNext = () => {
    // 답을 선택하지 않고 다음으로 넘어가려고 할 때
    if (!selectedAnswer && !showExplanation) {
      // 자동으로 오답 처리
      saveWrongAnswer(currentQuestion.id);
      setShowExplanation(true);
      return;
    }

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else if (studyMode === "all") {
      markCategoryAsCompleted();
      setShowCompletionModal(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  };

  const handleCheckAnswer = () => {
    const isCorrect = selectedAnswer === currentQuestion.correctAnswer;

    if (isCorrect) {
      if (studyMode === "wrong") {
        removeFromWrongAnswers(currentQuestion.id);
      }
    } else {
      saveWrongAnswer(currentQuestion.id);
    }

    setShowExplanation(true);
  };

  return (
    <div className="grammar-question">
      <button className="back-button" onClick={onBack}>
        ← 뒤로가기
      </button>

      <div className="question-container">
        <h3>
          Question {currentQuestion.id} ({currentQuestionIndex + 1} /{" "}
          {questions.length})
        </h3>

        <button
          className="toggle-korean-button"
          onClick={() => setShowKoreanQuestion(!showKoreanQuestion)}
        >
          {showKoreanQuestion ? "한글 질문 숨기기" : "한글 질문 보기"}
        </button>

        {showKoreanQuestion && (
          <p className="korean-question-text">
            {currentQuestion.koreanQuestion}
          </p>
        )}

        <p className="question-text">{currentQuestion.question}</p>

        <div className="choices-container">
          {Object.entries(currentQuestion.choices).map(([key, value]) => (
            <button
              key={key}
              className={`choice-button ${
                selectedAnswer === key
                  ? showExplanation
                    ? key === currentQuestion.correctAnswer
                      ? "correct"
                      : "wrong"
                    : "selected"
                  : ""
              }`}
              onClick={() => handleAnswerSelect(key)}
              disabled={showExplanation}
            >
              {key}. {value}
            </button>
          ))}
        </div>

        {selectedAnswer && !showExplanation && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
              marginTop: "20px",
            }}
          >
            <button className="check-answer-button" onClick={handleCheckAnswer}>
              정답 확인
            </button>
          </div>
        )}

        {showExplanation && (
          <div className="explanation">
            <p>
              <strong>정답:</strong> {currentQuestion.correctAnswer}.{" "}
              {currentQuestion.choices[currentQuestion.correctAnswer]}
            </p>
            <p>
              <strong>설명:</strong>
              {currentQuestion.explanation.split("\n").map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  <br />
                </React.Fragment>
              ))}
            </p>
            <p>
              <strong>전체 해석:</strong> {currentQuestion.translation}
            </p>

            <div className="navigation-buttons">
              {currentQuestionIndex > 0 && (
                <button className="prev-button" onClick={handlePrevious}>
                  이전 문제
                </button>
              )}
              {currentQuestionIndex < questions.length - 1 && (
                <button className="next-button" onClick={handleNext}>
                  다음 문제
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 완료 모달 추가 */}
      <Modal
        isOpen={showCompletionModal}
        onRequestClose={() => {
          setShowCompletionModal(false);
          onBack();
        }}
        style={{
          overlay: {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            zIndex: 1000,
          },
          content: {
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "280px",
            height: "50px",
            backgroundColor: "#1a1a1a",
            padding: "16px",
            borderRadius: "12px",
            border: "1px solid #333",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            minHeight: "fit-content",
          },
        }}
        ariaHideApp={false}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "20px",
          }}
        >
          <span
            style={{
              color: "#fff",
              fontSize: "15px",
            }}
          >
            학습 완료 ✨
          </span>
          <button
            onClick={() => {
              setShowCompletionModal(false);
              onBack();
            }}
            style={{
              backgroundColor: "#333",
              color: "#fff",
              padding: "8px 16px",
              borderRadius: "6px",
              border: "none",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            확인
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Part5_Grammar;
